/**
 * Manual Creation in quantities (MDOT-25812): the composition editor in quantity mode, for a portfolio
 * that does not exist yet or for a draft created in quantities. A new composition starts by asking for
 * its AUM and base currency; a draft reads both back. The currency stays fixed from then on, and the AUM
 * changes only when the negative-cash banner raises it. Every save sends
 * the AUM on its AUM custom attribute; creating the portfolio or the draft also sends `quantityMode: true`
 * and the top-level `aum`, which is the one the backend validates.
 *
 * `pages/Portfolios/ManualCreation` renders this instead of its weight editor for the composition it
 * decides is in quantities.
 */
import { reportPlatformError } from "$root/api/error-reporting";
import { hasAccess } from "$root/components/AuthorizationGuard";
import { useCrumbs } from "$root/components/Crumbs/useCrumbs";
import { LeavePrompt } from "$root/components/LeavePrompt";
import { PageHeader } from "$root/components/PageHeader";
import { typedUrlForRoute, useTypedNavigation } from "$root/components/PlatformRouter/RoutesDef";
import { ReactQueryWrapperBase } from "$root/components/ReactQueryWrapper";
import { type CompareDataItem, CompareOverlay } from "$root/functional-areas/compare-portfolio/CompareOverlay";
import {
	INVESTMENT_INSTRUMENT_LIMIT,
	MIN_NUMBER_OF_INSTRUMENTS,
	MIN_NUMBER_OF_INVESTMENT_INSTRUMENTS,
} from "$root/functional-areas/const";
import {
	buildAumAttributeValue,
	investmentAum,
	resolveCustomAttributesWithAum,
} from "$root/functional-areas/customAttributes/aum";
import { investmentCustomAttributesRepository } from "$root/functional-areas/customAttributes/repository";
import { createInstrumentList, instrumentsRepository } from "$root/functional-areas/instruments/repository";
import { userClassifications$ } from "$root/functional-areas/instruments/user-classifications";
import { customerDecimalPlaces, useUserValue } from "$root/functional-areas/user";
import { platformToast } from "$root/notification-system/toast";
import { countInstrumentInComposition } from "$root/pages/Portfolios/common";
import { generateDefaultManualCreation } from "$root/pages/Portfolios/ManualCreation/draftDefaults";
import {
	type SubmitManualCreationPayload,
	spawnSubmitManualCreationDialog,
} from "$root/pages/Portfolios/ManualCreation/spawn-createPortfolio";
import { usePortfolioStudioRedirect } from "$root/pages/PortfoliosStudio/usePortfolioStudioRedirect";
import { trackMixPanelEvent } from "$root/third-party-integrations/initMixPanel";
import { useQueryNoRefetch, useQueryReactor } from "$root/third-party-integrations/react-query/hooks";
import { columnsDefinition } from "$root/ui-lib/table/column";
import type { Currencies, CustomAttributeValue, InvestmentSummary } from "@eu.mdotm.api/sphere";
import { useAtomValue } from "@mdotm/shadcn/legacy/reactivity";
import { Button } from "@mdotm/shadcn/mdotm/components";
import type { AxiosError } from "axios";
import { Map } from "immutable";
import { useEffect, useMemo, useRef, useState } from "react";
import { flushSync } from "react-dom";
import { useTranslation } from "react-i18next";
import type { EditorBannersHandle } from "../../banners/EditorBanners";
import { cashTickerToInstrument } from "../../builder";
import type { Instrument } from "../../const";
import { type DraftInvestmentSave, instrumentsEditorRepository, type NewInvestmentSave } from "../../repository";
import InstrumentCompositionEditor from "../../instrumentEditor";
import ExposureSankeyBlock from "../../InstrumentExposureSankey";
import { useQuantityEditorBuilder } from "../builder";
import { withQuantityCommitHandlers } from "../columns";
import { compositionPassesSubmitChecks, resetQuantityComposition, serializeComposition } from "../editorHost";
import { withHeldIssues } from "../held-issues/heldIssue";
import { spawnCreationAumDialog } from "./CreationAumDialog";
import { withSavedRiskModels } from "./savedRiskModel";

export type QuantityManualCreationProps = {
	workspaceUid?: string;
	intentId?: string;
	/** The draft being resumed, with its summary; absent for a new composition. */
	draft?: { uuid: string; summary: InvestmentSummary };
};

/** What the editor opens on: the rows and the cash, and for a draft the AUM it was saved with. */
type InitialComposition = {
	instruments: Instrument[];
	cash: Instrument | undefined;
	cashTickerName: string | undefined;
	aum: number | null;
};

async function loadDraftComposition(uuid: string): Promise<InitialComposition> {
	const [editComposition, aum] = await Promise.all([
		instrumentsRepository.editComposition.fetchQuery(uuid, "INVESTMENT_DRAFT"),
		investmentAum.fetchQuery(uuid, "INVESTMENT_DRAFT"),
	]);
	const composition = editComposition.composition ?? [];
	const [userInstrumentsMap, investmentData] = await Promise.all([
		instrumentsRepository.userClassifications.fetchQuery(
			composition.map((entry) => entry.ticker).filter((ticker) => ticker != null),
		),
		instrumentsRepository.investmentsRichInfo.fetchQuery(composition),
	]);
	return {
		instruments: withSavedRiskModels(
			withHeldIssues(createInstrumentList({ composition, userInstrumentsMap, investmentData }), composition),
			composition,
		),
		cash: editComposition.cashTicker ? cashTickerToInstrument(editComposition.cashTicker) : undefined,
		cashTickerName: editComposition.cashTicker?.ticker,
		aum,
	};
}

/**
 * A new composition: no rows yet, and its cash held in the AUM's currency. The backend's blank
 * composition always names its default cash, so the row is named here, by the backend's
 * `CASH_<currency>` ticker convention (`../cashRow`).
 */
function newComposition(currency: Currencies): InitialComposition {
	return {
		instruments: [],
		cash: cashTickerToInstrument({ ticker: `CASH_${currency}`, instrument: `Cash ${currency}`, currency }),
		cashTickerName: undefined,
		aum: null,
	};
}

/** The AUM entry a brand-new portfolio is saved with; nothing while the tenant has no AUM attribute. */
async function newAumAttributes(aum: number): Promise<CustomAttributeValue[] | undefined> {
	const value = buildAumAttributeValue(await investmentCustomAttributesRepository.customAttributes.fetchQuery(), aum);
	return value ? [value] : undefined;
}

async function reportingSaveErrors(save: () => Promise<void>): Promise<void> {
	try {
		await save();
	} catch (error) {
		const message = (error as AxiosError<{ message?: string }>).response?.data.message;
		platformToast({
			children:
				message === "maxValue should be greater or equal to 0.02"
					? "Please review your instrument, minimum weight acceptable is 0.02"
					: "Something went wrong, please try later",
			severity: "error",
			icon: "Portfolio",
		});
		reportPlatformError(error, "ERROR", "portfolio", { message: "unable to create portfolio in quantities" });
		throw error;
	}
}

export function QuantityManualCreation({
	workspaceUid,
	intentId,
	draft,
}: QuantityManualCreationProps): React.JSX.Element {
	const { t } = useTranslation();
	const { push } = useTypedNavigation();
	const user = useUserValue();
	const portfolioStudio = usePortfolioStudioRedirect(workspaceUid);
	const crumbs = useCrumbs();
	const workspacePath = crumbs.useWorkspacePath(workspaceUid);
	const classifications = useAtomValue(userClassifications$) ?? [];

	const instrumentBuilder = useQuantityEditorBuilder({ composition: Map() });
	const editorBannersRef = useRef<EditorBannersHandle>(null);
	const [pathToNotBlock, setPathToNotBlock] = useState<string[]>([typedUrlForRoute("Login", {})]);
	const [expandExposure, setExpandExposure] = useState(false);
	// The currency the AUM is held in, once the builder holds the composition in quantities. The editor
	// is not shown before: a new composition has no budget to size anything against until it is asked for.
	const [currency, setCurrency] = useState<Currencies | null>(null);

	const maxInstrumentsInComposition = hasAccess(user, { requiredService: "NUMBER_OF_INSTRUMENTS_CHECK_BYPASS" })
		? +Infinity
		: INVESTMENT_INSTRUMENT_LIMIT;
	const minInstrumentsInComposition = hasAccess(user, { requiredService: "NUMBER_OF_INSTRUMENTS_CHECK_BYPASS" })
		? MIN_NUMBER_OF_INSTRUMENTS
		: MIN_NUMBER_OF_INVESTMENT_INSTRUMENTS;

	const queryInitial = useQueryNoRefetch({
		queryKey: ["quantityManualCreation", draft?.uuid ?? null],
		queryFn: () => (draft ? loadDraftComposition(draft.uuid) : null),
	});

	// The AUM prompt outlives nothing: leaving the page any other way than through it takes it down.
	const [leaving] = useState(() => new AbortController());
	useEffect(() => () => leaving.abort(), [leaving]);

	// Started once: a refetch must neither ask for the AUM again nor reset a composition being edited.
	const started = useRef(false);
	useQueryReactor(queryInitial, {
		onSuccess(initial) {
			if (started.current) {
				return;
			}
			started.current = true;
			if (draft && initial) {
				resetQuantityComposition(instrumentBuilder, {
					...initial,
					currency: draft.summary.baseCurrency,
					lastTradeDate: draft.summary.lastTradeDate,
				});
				setCurrency(draft.summary.baseCurrency ?? null);
				if (initial.instruments.some((x) => x.instrumentType === "INVESTMENT")) {
					setExpandExposure(true);
				}
				return;
			}
			spawnCreationAumDialog({ onLeave: () => portfolioStudio.navigate(), signal: leaving.signal })
				.promise.then((chosen) => {
					if (chosen == null) {
						return;
					}
					resetQuantityComposition(instrumentBuilder, {
						...newComposition(chosen.currency),
						aum: chosen.aum,
						currency: chosen.currency,
						lastTradeDate: undefined,
					});
					setCurrency(chosen.currency);
				})
				.catch(() => undefined);
		},
	});

	const goToPortfolio = (portfolioUid: string) => {
		flushSync(() => setPathToNotBlock([typedUrlForRoute("PortfolioDetails", { portfolioUid })]));
		if (workspaceUid) {
			push("PortfolioDetailsWithWorkspace", { workspaceUid, portfolioUid });
		} else {
			push("PortfolioDetails", { portfolioUid });
		}
	};

	const onSubmit = (form: SubmitManualCreationPayload) =>
		reportingSaveErrors(async () => {
			// A value typed into a quantity cell is still Cold War's to size; the save is of its answer.
			await instrumentBuilder.settleSizing();
			if (form.submitMode === "save-as-portfolio") {
				const nOfInstrumentsInComposition = countInstrumentInComposition(
					instrumentBuilder.getComposition({ removeDeleted: true }),
					form.portfolioSavingMode ?? "NEST_PORTFOLIOS",
				);
				if (
					!compositionPassesSubmitChecks(
						instrumentBuilder,
						nOfInstrumentsInComposition,
						{
							minInstrumentsInComposition,
							maxInstrumentsInComposition,
							weightTotalPrecision: customerDecimalPlaces(user),
						},
						editorBannersRef,
					)
				) {
					return;
				}
			}
			const composition = serializeComposition(instrumentBuilder);
			const aum = instrumentBuilder.getAum();

			if (draft) {
				const save = {
					form,
					composition,
					uuid: draft.uuid,
					customAttributes: await resolveCustomAttributesWithAum(draft.uuid, aum, "INVESTMENT_DRAFT"),
				} satisfies DraftInvestmentSave;
				if (form.submitMode === "save-as-draft") {
					await instrumentsEditorRepository.draft.update(save);
					platformToast({
						children: `Portfolio ${form.name} saved successfully`,
						severity: "success",
						icon: "Portfolio",
					});
					return;
				}
				const { identifier } = await instrumentsEditorRepository.draft.saveAsInvestment(save);
				goToPortfolio(identifier!);
				return;
			}

			if (aum == null) {
				// The AUM dialog sets it before the composition can be edited, so this is never a user error.
				throw new Error("A portfolio in quantities cannot be created without its AUM");
			}
			const save = {
				form,
				composition,
				quantityMode: true,
				aum,
				customAttributes: await newAumAttributes(aum),
			} satisfies NewInvestmentSave;
			if (form.submitMode === "save-as-draft") {
				const { identifier } = await instrumentsEditorRepository.create.investmentDraft(save);
				trackMixPanelEvent("portfolio.entityActions.createAsDraftManual", {
					intentId,
					portfolio: { uuid: identifier!, name: form.name! },
				});
				const draftRoute = workspaceUid
					? typedUrlForRoute("Portfolios/ManualPortfolioCreationWithWorkspace", { workspaceUid, uuid: identifier })
					: typedUrlForRoute("Portfolios/ManualPortfolioCreation", { uuid: identifier });
				flushSync(() => setPathToNotBlock([draftRoute]));
				platformToast({
					children: `Portfolio ${form.name} saved successfully`,
					severity: "success",
					icon: "Portfolio",
				});
				if (workspaceUid) {
					push("Portfolios/ManualPortfolioCreationWithWorkspace", { workspaceUid, uuid: identifier });
				} else {
					push("Portfolios/ManualPortfolioCreation", { uuid: identifier });
				}
				return;
			}
			const { identifier } = await instrumentsEditorRepository.create.investment(save);
			trackMixPanelEvent("portfolio.entityActions.createAsPortfolioManual", {
				intentId,
				portfolio: { uuid: identifier!, name: form.name! },
			});
			goToPortfolio(identifier!);
		});

	const comparedInvestments = useMemo((): CompareDataItem[] => {
		const investmentsCompared = instrumentBuilder
			.getComposition()
			.filter((x) => instrumentBuilder.getComparedInstrument(x.id) !== undefined)
			.values();
		return Array.from(investmentsCompared).map((instrument) => ({
			id: instrument.id,
			composition:
				instrument.macroAssetClassExposure?.map((x) => ({ quality: x.firstQualityLevel, weight: x.weight })) ?? [],
			numberOfInstrument: instrument.nofInstruments ?? 0,
			portfolioName: instrument.name ?? "-",
			uuid: instrument.investmentUuid,
			note: instrument.lastActionNote,
			action: instrument.action,
		}));
	}, [instrumentBuilder]);

	// The commit handlers capture `instrumentBuilder` without depending on it: its methods are
	// staleness-safe, and re-deriving the column defs on every edit would make AG Grid reprocess its
	// columns mid-editing.
	// biome-ignore lint/correctness/useExhaustiveDependencies: instrumentBuilder's methods are staleness-safe
	const columns = useMemo(
		() =>
			withQuantityCommitHandlers(
				columnsDefinition.instrumentQuantityComposition(classifications, "edit", { currency }),
				instrumentBuilder,
			),
		[classifications, currency],
	);

	return (
		<>
			<PageHeader
				crumbs={[
					...crumbs.portfolioStudio("Portfolios", draft?.uuid),
					...workspacePath,
					...(draft ? crumbs.portfolioName(draft.summary) : crumbs.newPortfolio),
				]}
				titleAction={
					<div className="flex items-center space-x-2">
						<Button
							size="small"
							onClick={() => portfolioStudio.navigate()}
							palette="tertiary"
							data-qualifier="CompositionEditor/Cancel"
						>
							{t("BUTTON.CANCEL")}
						</Button>
						<Button
							size="small"
							palette="primary"
							disabled={currency == null}
							onClick={() =>
								spawnSubmitManualCreationDialog({
									entity: draft ? "INVESTMENT_DRAFT" : "INVESTMENT",
									...(draft ? { defaultManualCreation: generateDefaultManualCreation(draft.summary) } : {}),
									quantities: { currency: currency! },
									includesInvestments: instrumentBuilder
										.getComposition()
										.some((instrument) => instrument.proxyOverwriteType === "PORTFOLIO_MIXED"),
									onAsyncSubmit: onSubmit,
								})
							}
							data-qualifier="CompositionEditor/Save"
						>
							{t("BUTTON.DONE")}
						</Button>
					</div>
				}
			/>
			<ReactQueryWrapperBase query={queryInitial}>
				{() =>
					currency == null ? null : (
						<>
							<div className="min-h-0 grow flex">
								<InstrumentCompositionEditor
									entity={draft ? "INVESTMENT_DRAFT" : "INVESTMENT"}
									{...(draft ? { mode: "edit" as const, uuid: draft.uuid } : { mode: "new" as const })}
									instrumentBuilder={instrumentBuilder}
									editorBannersRef={editorBannersRef}
									minThreshold={MIN_NUMBER_OF_INVESTMENT_INSTRUMENTS}
									limit={maxInstrumentsInComposition}
									columns={columns}
									compositionMode="quantity"
								/>
								<ExposureSankeyBlock
									expand={expandExposure}
									onExpandChange={setExpandExposure}
									instrumentBuilder={instrumentBuilder}
								/>
							</div>
							<CompareOverlay
								show={comparedInvestments.length > 0}
								onClose={() => instrumentBuilder.clearComparedInstruments()}
								onRemove={(id) => instrumentBuilder.deleteComparedInstruments(id)}
								compareData={comparedInvestments}
							/>
						</>
					)
				}
			</ReactQueryWrapperBase>
			<LeavePrompt
				title={t("NOTIFICATION_SETTINGS.LEAVE_PROMPT.TITLE")}
				when={instrumentBuilder.isDirty}
				pathToNotBlock={pathToNotBlock}
			>
				{t("NOTIFICATION_SETTINGS.LEAVE_PROMPT.MESSAGE")}
			</LeavePrompt>
		</>
	);
}
